import { useState, useCallback, useMemo } from 'react'

export interface UseQlikScriptOptions {
  /**
   * Initial script content
   */
  initialScript?: string
  
  /**
   * Callback when script content changes
   */
  onChange?: (script: string) => void
  
  /**
   * Available variables for validation
   */
  variables?: string[]
  
  /**
   * Enable syntax validation
   */
  enableValidation?: boolean
}

export interface QlikScriptError {
  line: number
  column: number
  message: string
  type: 'syntax' | 'warning' | 'info'
}

export interface UseQlikScriptReturn {
  /** Current script content */
  script: string
  
  /** Set script content */
  setScript: (script: string) => void
  
  /** Clear script content */
  clearScript: () => void
  
  /** Script validation errors */
  errors: QlikScriptError[]
  
  /** Whether script has any errors */
  hasErrors: boolean
  
  /** Format script with proper indentation */
  formatScript: () => void
  
  /** Get script statistics */
  getScriptStats: () => {
    lines: number
    characters: number
    words: number
    tables: number
    variables: string[]
  }
}

/**
 * Hook for managing Qlik script state and validation
 */
export function useQlikScript(options: UseQlikScriptOptions = {}): UseQlikScriptReturn {
  const {
    initialScript = '',
    onChange,
    variables = [],
    enableValidation = true
  } = options

  const [script, setScriptState] = useState(initialScript)

  const setScript = useCallback((newScript: string) => {
    setScriptState(newScript)
    onChange?.(newScript)
  }, [onChange])

  const clearScript = useCallback(() => {
    setScript('')
  }, [setScript])

  // Basic syntax validation
  const errors = useMemo((): QlikScriptError[] => {
    if (!enableValidation || !script.trim()) return []

    const errors: QlikScriptError[] = []
    const lines = script.split('\n')

    lines.forEach((line, index) => {
      const trimmedLine = line.trim()
      if (!trimmedLine) return

      // Check for missing semicolons on certain statements
      if (
        trimmedLine.match(/^(LOAD|SELECT|LET|SET|STORE|DROP)\s+/i) &&
        !trimmedLine.endsWith(';') &&
        !trimmedLine.endsWith(':') &&
        !trimmedLine.includes('FROM') // FROM statements might be multi-line
      ) {
        errors.push({
          line: index + 1,
          column: line.length,
          message: 'Missing semicolon',
          type: 'warning'
        })
      }

      // Check for unmatched parentheses
      const openParens = (line.match(/\(/g) || []).length
      const closeParens = (line.match(/\)/g) || []).length
      if (openParens !== closeParens) {
        errors.push({
          line: index + 1,
          column: line.indexOf('(') + 1,
          message: 'Unmatched parentheses',
          type: 'syntax'
        })
      }

      // Check for undefined variables
      const variableMatches = line.match(/\$\([^)]+\)/g)
      if (variableMatches) {
        variableMatches.forEach(match => {
          const varName = match.slice(2, -1)
          if (!variables.includes(varName)) {
            errors.push({
              line: index + 1,
              column: line.indexOf(match) + 1,
              message: `Undefined variable: ${varName}`,
              type: 'warning'
            })
          }
        })
      }
    })

    return errors
  }, [script, variables, enableValidation])

  const hasErrors = useMemo(() => 
    errors.some(error => error.type === 'syntax')
  , [errors])

  const formatScript = useCallback(() => {
    const lines = script.split('\n')
    let indentLevel = 0
    const indentSize = 2

    const formattedLines = lines.map(line => {
      const trimmed = line.trim()
      if (!trimmed) return ''

      // Decrease indent for END statements
      if (trimmed.match(/^(END\s+(IF|SUB|SWITCH))/i)) {
        indentLevel = Math.max(0, indentLevel - 1)
      }

      const indentedLine = ' '.repeat(indentLevel * indentSize) + trimmed

      // Increase indent for control structures
      if (
        trimmed.match(/^(IF\s+|FOR\s+|DO\s*$|WHILE\s+|SUB\s+|SWITCH\s+)/i) ||
        trimmed.match(/^(THEN\s*$|ELSE\s*$)/i)
      ) {
        indentLevel++
      }

      return indentedLine
    })

    setScript(formattedLines.join('\n'))
  }, [script, setScript])

  const getScriptStats = useCallback(() => {
    const lines = script.split('\n').filter(line => line.trim()).length
    const characters = script.length
    const words = script.split(/\s+/).filter(word => word).length
    
    // Count LOAD/SELECT statements as tables
    const tableMatches = script.match(/^(LOAD|SELECT)\s+/gmi) || []
    const tables = tableMatches.length
    
    // Extract variable definitions
    const variableMatches = script.match(/^(LET|SET)\s+([^=\s]+)/gmi) || []
    const scriptVariables = variableMatches.map(match => {
      const parts = match.split(/\s+/)
      return parts[1] || ''
    }).filter(Boolean)

    return {
      lines,
      characters,
      words,
      tables,
      variables: scriptVariables
    }
  }, [script])

  return {
    script,
    setScript,
    clearScript,
    errors,
    hasErrors,
    formatScript,
    getScriptStats
  }
}